// src/components/Admin/Customer_Master/CustomerView.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  MoreVertical,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit2,
  Trash2,
  User,
  Phone,
  Mail,
  MapPin,
  Coins,
  Loader2,
  Building2,
} from "lucide-react";
import toast from "react-hot-toast";
import CustomerAddEdit from "./CustomerAddEdit";
import CustomerDetailsModal from "@/components/shared/CustomerDetailsModal";
import { ConfirmDialog } from "../Menu_Management/UIComponents";
import Pagination from "@/components/shared/Pagination";
import useCustomer from "@/hooks/useCustomer";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

export default function CustomerList({ renderActions, selectedBranchId, isOwner }) {
  const { hasPermission } = useAuth();

  // Permission flags
  const canAdd    = hasPermission(P.CRM.CUSTOMERS.ADD);
  const canEdit   = hasPermission(P.CRM.CUSTOMERS.EDIT);
  const canDelete = hasPermission(P.CRM.CUSTOMERS.DELETE);

  // Only show the action menu column if the user has at least edit or delete
  const hasAnyRowAction = canEdit || canDelete;

  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: "",
    data: null,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Details modal state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  const {
    customers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    loading,
    fetchCustomers,
  } = useCustomer();

  // Re-fetch when branch filter changes
  useEffect(() => {
    const filters = {};
    if (selectedBranchId) filters.branchId = selectedBranchId;
    fetchCustomers(filters);
  }, [selectedBranchId, fetchCustomers]);

  // Expose actions to parent for header buttons
  // Only expose onAdd if the user actually has the ADD permission
  useEffect(() => {
    if (renderActions) {
      renderActions({
        ...(canAdd && {
          onAdd: () => {
            setShowForm(true);
            setEditItem(null);
          },
        }),
        onRefresh: () => {
          const filters = {};
          if (selectedBranchId) filters.branchId = selectedBranchId;
          fetchCustomers(filters);
          showToast("Customers refreshed", "success");
        },
      });
    }
  }, [renderActions, fetchCustomers, selectedBranchId, canAdd]);

  const handleSave = async (data) => {
    try {
      if (editItem) {
        await updateCustomer(editItem.id, data);
      } else {
        await createCustomer(data);
      }
      setShowForm(false);
      setEditItem(null);
    } catch (error) {
      console.error("Failed to save customer:", error);
      // Error toast is already shown in useCustomer hook
    }
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest(".action-menu-container")) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Filter customers based on search text
  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const text = filterText.toLowerCase();
      return (
        customer.name?.toLowerCase().includes(text) ||
        String(customer.phone ?? "").toLowerCase().includes(text) ||
        (customer.email || "").toLowerCase().includes(text) ||
        (customer.address || "").toLowerCase().includes(text) ||
        (customer.notes || "").toLowerCase().includes(text)
      );
    });
  }, [customers, filterText]);

  // Sort customers
  const sortedCustomers = useMemo(() => {
    let sortable = [...filteredCustomers];

    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";

        const aNum = Number(aValue);
        const bNum = Number(bValue);
        const bothNumeric =
          !isNaN(aNum) && !isNaN(bNum) && aValue !== "" && bValue !== "";

        if (bothNumeric) {
          if (aNum < bNum) return sortConfig.direction === "asc" ? -1 : 1;
          if (aNum > bNum) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }

        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return sortable;
  }, [filteredCustomers, sortConfig]);

  // Keep currentPage in range when list / pageSize changes
  const totalItems = sortedCustomers.length;
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / pageSize);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Paginated slice of customers
  const paginatedCustomers = useMemo(() => {
    if (loading) return [];
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedCustomers.slice(startIndex, endIndex);
  }, [sortedCustomers, currentPage, pageSize, loading]);

  // Handle sort
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={14} className="text-teal-600" />
    ) : (
      <ArrowDown size={14} className="text-teal-600" />
    );
  };

  // Open confirm dialog — guard with permission
  const handleDeleteCustomer = (id) => {
    if (!canDelete) {
      toast.error("You don't have permission to delete customers");
      return;
    }
    setConfirmDialog({
      isOpen: true,
      type: "delete",
      data: id,
    });
  };

  // Actual delete logic
  const confirmDelete = async () => {
    try {
      await deleteCustomer(confirmDialog.data);
      showToast("Customer deleted", "success");
    } catch (error) {
      showToast("Delete failed", "error");
    } finally {
      setConfirmDialog({ isOpen: false, type: "", data: null });
    }
  };

  // Config for ConfirmDialog
  const confirmConfig =
    confirmDialog.type === "delete"
      ? {
          title: "Delete Customer",
          message:
            "Are you sure you want to delete this customer? This action cannot be undone.",
          confirmText: "Delete",
          type: "danger",
          onConfirm: confirmDelete,
        }
      : null;

  // Helper to open details modal
  const openDetailsModal = (customer) => {
    setSelectedCustomer({ ...customer });
    setShowDetailModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailModal(false);
    setSelectedCustomer(null);
  };

  const formatLoyalty = (v) => {
    const n = Number(v);
    if (isNaN(n)) return "0";
    return n.toLocaleString();
  };

  const prettyCustomerType = (type) => {
    if (!type) return "Regular";
    if (type.toLowerCase() === "vip") return "VIP";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getCustomerTypeStyles = (type) => {
    if (!type || type === "regular") return "bg-slate-100 text-slate-700";
    if (type.toLowerCase() === "vip") return "bg-amber-100 text-amber-700";
    if (type.toLowerCase() === "corporate")
      return "bg-blue-100 text-blue-700";
    return "bg-slate-100 text-slate-700";
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Compute colspan dynamically based on visible columns
  // Base cols: Customer, Phone, Email, Type, Points = 5
  // + Branch (isOwner) + Actions (hasAnyRowAction)
  const colSpan = 5 + (isOwner ? 1 : 0) + (hasAnyRowAction ? 1 : 0);

  return (
    <div className="min-h-full bg-gray-50 px-4 py-6">
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by name, phone, email, address..."
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white transition-all"
            />
          </div>
          {filterText && (
            <button
              onClick={() => {
                setFilterText("");
                setCurrentPage(1);
              }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto thin-scrollbar">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-b from-gray-50 to-gray-100 border-b border-gray-200">
                  <th
                    onClick={() => handleSort("name")}
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Customer
                      {getSortIcon("name")}
                    </div>
                  </th>

                  <th
                    onClick={() => handleSort("phone")}
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Phone
                      {getSortIcon("phone")}
                    </div>
                  </th>

                  <th
                    onClick={() => handleSort("email")}
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Email
                      {getSortIcon("email")}
                    </div>
                  </th>

                  {/* Branch column — only for owners */}
                  {isOwner && (
                    <th
                      onClick={() => handleSort("branchName")}
                      className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        Branch
                        {getSortIcon("branchName")}
                      </div>
                    </th>
                  )}

                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Type
                  </th>

                  <th
                    onClick={() => handleSort("loyaltyPoints")}
                    className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      Points
                      {getSortIcon("loyaltyPoints")}
                    </div>
                  </th>

                  {/* Actions column — only if user can edit or delete */}
                  {hasAnyRowAction && (
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td
                      className="px-4 py-16 text-center text-gray-500"
                      colSpan={colSpan}
                    >
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 size={36} className="text-teal-600 animate-spin" />
                        <p className="text-sm font-medium">Loading customers...</p>
                      </div>
                    </td>
                  </tr>
                ) : sortedCustomers.length === 0 ? (
                  <tr>
                    <td
                      className="px-4 py-16 text-center text-gray-500"
                      colSpan={colSpan}
                    >
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            No customers found
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {filterText
                              ? "Try adjusting your search terms"
                              : "Get started by adding your first customer"}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-teal-50/40 transition-colors group"
                    >
                      {/* Customer Name */}
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openDetailsModal({ ...customer })}
                          className="flex items-center gap-3 text-left w-full hover:opacity-90 focus:outline-none"
                        >
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-teal-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors truncate">
                              {customer.name}
                            </p>
                            {customer.address && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate flex items-center gap-1">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{customer.address}</span>
                              </p>
                            )}
                          </div>
                        </button>
                      </td>

                      {/* Phone */}
                      <td className="px-4 py-3">
                        {customer.phone ? (
                          <div className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            {customer.phone}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>

                      {/* Email */}
                      <td className="px-4 py-3">
                        {customer.email ? (
                          <div className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate max-w-[180px]">
                              {customer.email}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>

                      {/* Branch (Owner only) */}
                      {isOwner && (
                        <td className="px-4 py-3">
                          {customer.branchName ? (
                            <div className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                              <Building2 className="w-3.5 h-3.5 text-gray-400" />
                              <span className="truncate max-w-[150px]">{customer.branchName}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                      )}

                      {/* Customer Type */}
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getCustomerTypeStyles(
                            customer.customerType
                          )}`}
                        >
                          {prettyCustomerType(customer.customerType)}
                        </span>
                      </td>

                      {/* Loyalty Points */}
                      <td className="px-4 py-3 text-center">
                        <div className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
                          <Coins className="w-4 h-4 text-amber-500" />
                          {formatLoyalty(customer.loyaltyPoints)}
                        </div>
                      </td>

                      {/* Actions — only rendered if user has edit or delete permission */}
                      {hasAnyRowAction && (
                        <td className="px-4 py-3 text-center">
                          <div className="inline-block action-menu-container">
                            <button
                              data-menu-id={customer.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openActionMenu === customer.id) {
                                  setOpenActionMenu(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setMenuPosition({
                                    top: rect.bottom + 4,
                                    left: rect.right - 160,
                                  });
                                  setOpenActionMenu(customer.id);
                                }
                              }}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <MoreVertical size={16} className="text-gray-500" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Fixed Position Action Menu - Rendered outside table */}
        {openActionMenu !== null && (
          <div
            className="fixed bg-white shadow-xl border border-gray-200 rounded-lg w-40 z-[9999] overflow-hidden animate-fadeIn"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
          >
            {/* Edit — only if canEdit */}
            {canEdit && (
              <button
                onClick={() => {
                  const customer = customers.find(c => c.id === openActionMenu);
                  if (customer) {
                    setEditItem({ ...customer });
                    setShowForm(true);
                  }
                  setOpenActionMenu(null);
                }}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
              >
                <Edit2 size={14} />
                Edit
              </button>
            )}

            {/* Divider — only if both actions exist */}
            {canEdit && canDelete && (
              <div className="border-t border-gray-100" />
            )}

            {/* Delete — only if canDelete */}
            {canDelete && (
              <button
                onClick={() => {
                  handleDeleteCustomer(openActionMenu);
                  setOpenActionMenu(null);
                }}
                className="w-full text-left px-4 py-2.5 hover:bg-red-50 text-sm text-red-600 flex items-center gap-2"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {!loading && sortedCustomers.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl px-4 shadow-sm">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          </div>
        )}
      </div>

      {/* Customer Details Modal — pass canEdit so the modal's Edit button respects permissions */}
      <CustomerDetailsModal
        isOpen={showDetailModal}
        onClose={closeDetailsModal}
        customer={selectedCustomer}
        onEdit={(customer) => {
          setEditItem({ ...customer });
          setShowForm(true);
        }}
        showEditButton={canEdit}
      />

      {/* Add/Edit Form Modal — only mount if user has the right permission */}
      {showForm && (canAdd || canEdit) && (
        <CustomerAddEdit
          item={editItem}
          onClose={() => {
            setShowForm(false);
            setEditItem(null);
          }}
          onSave={handleSave}
        />
      )}

      {/* Confirm dialog */}
      {confirmConfig && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() =>
            setConfirmDialog({ isOpen: false, type: "", data: null })
          }
          {...confirmConfig}
        />
      )}
    </div>
  );
}